import { WaveformData, RegionState } from "./types";
import { CURRENT_WAVEFORM_VERSION } from "./calculateWaveform";

const STEP = 10000;
const DEFAULT_SAMPLE_RATE = 48000;

/**
 * Combine per-region waveforms into a single "master" waveform that represents
 * the full mix across all tracks.
 *
 * Each region's waveform is positioned on the master timeline according to its
 * `position` value, and overlapping regions are summed (as with real audio
 * mixing). The result is clamped to [-1, 1].
 *
 * This is a **static full-mix** — it does not account for per-track gain,
 * mute, solo, or master gain. It always shows the full combined waveform.
 *
 * @param waveforms  Waveform data keyed by region ID
 * @param regions    Region state keyed by region ID
 * @param duration   Total song duration in seconds
 * @param sampleRate Audio context sample rate (default 48000)
 * @returns Combined master waveform, or null if there are no regions/waveforms
 */
export function combineMasterWaveform(
  waveforms: Record<string, WaveformData>,
  regions: Record<string, RegionState>,
  duration: number,
  sampleRate: number = DEFAULT_SAMPLE_RATE,
): WaveformData | null {
  const regionList = Object.values(regions);
  if (regionList.length === 0 || duration <= 0) return null;

  const totalColumns = Math.floor(duration * sampleRate / STEP);
  if (totalColumns <= 0) return null;

  // Pre-allocate min/max arrays for fast accumulation
  const mins = new Float64Array(totalColumns); // initialized to 0
  const maxs = new Float64Array(totalColumns); // initialized to 0

  let hasData = false;

  for (const region of regionList) {
    const waveform = waveforms[region.id];
    if (!waveform || waveform.data.length === 0) continue;

    const startCol = Math.floor(region.position * sampleRate / STEP);
    const regionData = waveform.data;

    for (let j = 0; j < regionData.length; j++) {
      const targetCol = startCol + j;
      if (targetCol < 0 || targetCol >= totalColumns) continue;

      mins[targetCol] += regionData[j][0];
      maxs[targetCol] += regionData[j][1];
      hasData = true;
    }
  }

  if (!hasData) return null;

  // Build final data array with clamping
  const data: number[][] = new Array(totalColumns);
  for (let i = 0; i < totalColumns; i++) {
    data[i] = [
      Math.max(-1, Math.min(1, mins[i])),
      Math.max(-1, Math.min(1, maxs[i])),
    ];
  }

  return {
    step: STEP,
    version: CURRENT_WAVEFORM_VERSION,
    data,
  };
}
